// src/components/onboarding/OnboardingChecklist.js
import React, { useState } from "react";
import { Check, ChevronDown, ArrowRight, X, Rocket } from "lucide-react";
import { useOnboarding } from "../../contexts/OnboardingContext";
import { useWsNavigate } from "../../hooks/useWsNavigate";
import OnboardingAutoGenerationCard from "./OnboardingAutoGenerationCard";

const PRIMARY_BUTTON =
  "px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors";
const OUTLINE_BUTTON =
  "px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors";

const Media = ({ media }) => {
  if (!media) return null;
  if (media.type === "code") {
    return (
      <pre className="mt-2 rounded-lg border border-border bg-muted p-3 overflow-x-auto text-xs text-foreground">
        <code>{media.content}</code>
      </pre>
    );
  }
  return (
    <img
      src={media.src}
      alt={media.alt}
      className="mt-2 rounded-lg border border-border max-w-full"
    />
  );
};

const OnboardingChecklist = ({ firstName }) => {
  const { steps, dismiss, skipStep } = useOnboarding();
  const navigate = useWsNavigate();

  // Open the first incomplete step by default (fall back to first step).
  const firstIncomplete = steps.find((s) => !s.done) || steps[0];
  const [openId, setOpenId] = useState(firstIncomplete?.id);

  const handleDismiss = () => {
    if (window.confirm("Hide setup guide? You can reopen it any time from the sidebar.")) {
      dismiss();
    }
  };

  const advance = (currentId) => {
    const idx = steps.findIndex((s) => s.id === currentId);
    const next = steps[idx + 1];
    if (next) setOpenId(next.id);
  };

  const greeting = firstName
    ? `${firstName}, you're a few steps away`
    : "You're a few steps away";

  return (
    <div id="onboarding-checklist" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2.5">
          <Rocket className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={1.75} />
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {greeting} from testing your first agent
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Complete these to get your agent under test. Steps check off automatically.
            </p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
        >
          <X className="h-3.5 w-3.5" /> Dismiss
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
        {steps.map((step, i) => {
          const open = openId === step.id;
          const isLast = i === steps.length - 1;
          const autoGen = step.autoGenerate && step.autoGeneration;
          const autoGenDone = autoGen?.status === "completed";
          return (
            <div key={step.id}>
              <button
                onClick={() => setOpenId(open ? null : step.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
              >
                {step.done ? (
                  <Check
                    className="h-4 w-4 text-emerald-500 flex-shrink-0"
                    strokeWidth={2.5}
                  />
                ) : (
                  <step.icon
                    className="h-4 w-4 text-muted-foreground flex-shrink-0"
                    strokeWidth={1.75}
                  />
                )}
                <span
                  className={`flex-1 text-sm font-medium ${
                    step.done ? "text-muted-foreground line-through" : "text-foreground"
                  }`}
                >
                  {step.title}
                </span>
                {step.skipped && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    Skipped
                  </span>
                )}
                <span className="text-xs text-muted-foreground">{step.estTime}</span>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-300 ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* grid-rows 0fr↔1fr gives a smooth height transition without JS measurement */}
              <div
                data-testid={`onboarding-panel-${step.id}`}
                data-open={open ? "true" : "false"}
                aria-hidden={!open}
                className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                  open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-4 pb-5 pl-12">
                    <p className="text-sm text-muted-foreground mb-4">{step.description}</p>
                    {autoGen && (
                      <>
                        <OnboardingAutoGenerationCard
                          noun={step.autoGenerate.noun}
                          status={autoGen.status}
                          count={autoGen.ids?.length}
                          agentName={autoGen.agentName}
                          onView={() => navigate(step.autoGenerate.viewHref)}
                        />
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                          Or do it yourself
                        </p>
                      </>
                    )}
                    <ol className="space-y-4 border-l border-border pl-5 ml-1">
                      {step.substeps.map((ss, si) => (
                        <li key={si} className="relative">
                          <span className="absolute -left-[27px] top-0 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-[10px] font-semibold text-muted-foreground">
                            {si + 1}
                          </span>
                          <p className="text-sm font-medium text-foreground">{ss.title}</p>
                          <p className="text-sm text-muted-foreground">{ss.body}</p>
                          <Media media={ss.media} />
                        </li>
                      ))}
                    </ol>
                    <div className="flex flex-wrap items-center gap-3 mt-5">
                      {/* Once auto-generated items exist, viewing them (in the card) is the main action. */}
                      <button
                        onClick={() => navigate(step.cta.href)}
                        className={autoGenDone ? OUTLINE_BUTTON : PRIMARY_BUTTON}
                      >
                        {step.cta.label}
                      </button>
                      {step.secondaryCta && (
                        <button
                          onClick={() => navigate(step.secondaryCta.href)}
                          className={OUTLINE_BUTTON}
                        >
                          {step.secondaryCta.label}
                        </button>
                      )}
                      <div className="ml-auto flex items-center gap-4">
                        {!step.done && (
                          <button
                            onClick={() => {
                              skipStep(step.id);
                              advance(step.id);
                            }}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Skip this step
                          </button>
                        )}
                        {!isLast && (
                          <button
                            onClick={() => advance(step.id)}
                            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Next step <ArrowRight className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
